import { Dismiss20Regular } from '@fluentui/react-icons';
import { useEffect, useRef, type ReactNode } from 'react';

interface ExplorerSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  eyebrow?: string;
  title: string;
  description?: string;
  children: ReactNode;
  widthClassName?: string;
  closeOnEscape?: boolean;
  restoreFocusOnClose?: boolean;
}

export function ExplorerSidePanel({
  isOpen,
  onClose,
  eyebrow,
  title,
  description,
  children,
  widthClassName = 'max-w-2xl',
  closeOnEscape = true,
  restoreFocusOnClose = true,
}: ExplorerSidePanelProps) {
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    return () => {
      if (!restoreFocusOnClose || !openerRef.current?.isConnected) {
        return;
      }

      openerRef.current.focus();
    };
  }, [isOpen, restoreFocusOnClose]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      onClose();
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeOnEscape, isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/40 backdrop-blur-sm" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        className={`flex h-full w-full ${widthClassName} flex-col border-l border-slate-200 bg-white shadow-2xl`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div className="min-w-0">
            {eyebrow && <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{eyebrow}</div>}
            <h3 className="mt-2 text-2xl font-semibold text-slate-900">{title}</h3>
            {description && <p className="mt-2 max-w-2xl text-sm text-slate-600">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 text-slate-700 transition hover:border-slate-400 hover:text-slate-900"
          >
            <Dismiss20Regular />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

export default ExplorerSidePanel;